import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import { contentService } from "../services/contentService";
import { paymentService } from "../services/paymentService";
import { useAuthContext } from "../components/AuthContext";

interface UseStripePaymentReturn {
  isLoadingStripe: boolean;
  isStripeCompleted: boolean;
  setIsStripeCompleted: (value: boolean) => void;
}

export function useStripePayment(
  finishedFetch: boolean,
  refreshContents: () => Promise<void>,
): UseStripePaymentReturn {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [isLoadingStripe, setIsLoadingStripe] = useState<boolean>(false);
  const [isStripeCompleted, setIsStripeCompleted] = useState<boolean>(false);
  const processedSessionIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const chapter = searchParams.get("chapter");
    const contentId = searchParams.get("id");

    if (!sessionId || !chapter || !contentId || !finishedFetch || !user) return;

    // 既に処理済みのセッションIDかチェック
    if (processedSessionIds.current.has(sessionId)) return;

    const handleStripePayment = async () => {
      // 処理開始時にセッションIDを記録
      processedSessionIds.current.add(sessionId);

      try {
        const isPurchased = await contentService.isPurchasedContent(
          user.uid,
          contentId,
          chapter,
        );

        if (!isPurchased) {
          setIsLoadingStripe(true);

          await paymentService.processStripePayment(
            user.uid,
            sessionId,
            contentId,
            chapter,
          );

          setIsStripeCompleted(true);

          // Analytics tracking
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).twq("event", "tw-ofkye-ofypq", {});
          logEvent(analytics, "completed_purchase", {
            timing: localStorage.getItem("purchase_button_click_location"),
            unpurchased: true,
          });

          await refreshContents();
          navigate(`?id=${contentId}&chapter=${chapter}`);
        } else {
          // Already purchased, redirect appropriately
          if (chapter !== "all") {
            navigate(`?id=${contentId}&chapter=${chapter}`);
          } else {
            navigate(`?id=${contentId}&chapter=1`);
          }
        }
      } catch (error) {
        console.error("Stripe payment error:", error);
        await paymentService.refundStripePayment(sessionId);
        alert(
          "決済処理中にエラーが発生したため、返金いたしました。\nしばらくしてから再度お試しください。",
        );
      } finally {
        setIsLoadingStripe(false);
      }
    };

    handleStripePayment();
  }, [finishedFetch, searchParams, user, navigate, refreshContents]);

  return {
    isLoadingStripe,
    isStripeCompleted,
    setIsStripeCompleted,
  };
}
